import { Injectable, inject } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { MbsSize, ModalService } from 'mbs-ui-kit';
import { TFARequireModalComponent } from 'mbs-ui-shared@components';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { CommandService } from 'mbs-ui-shared@modules/rmm/services/rmm-command.service';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import { BehaviorSubject, Observable, catchError, from, map, of, switchMap } from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class TFARMMPermissionHelperService {
  private modalService = inject(ModalService);
  private tfaService = inject(TFAService);
  private ability = inject(AbilityService);
  private commandService = inject(CommandService);
  private computersFacade = inject(ComputersFacade);
  private auth = inject(AuthFacade);

  public hidForTFA$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public is2FAPassedStream(alwaysRequired = false): Observable<boolean> {
    const stream$ = this.hidForTFA$.getValue() ? this.hidForTFA$ : this.getAnyComputerFromAvailableComputerListStream();

    return stream$.pipe(
      switchMap((hid) => {
        if (!hid) return of(false);

        if (this.commandService.canHidAccess(hid) && !alwaysRequired) return of(true);

        return this.initTwoFAModal([hid]);
      })
    );
  }

  private initTwoFAModal(hids: string[]): Observable<boolean> {
    if (!this.ability.can('read', '2FAEnabled')) {
      return from(
        this.modalService.openCustom(TFARequireModalComponent, {
          size: MbsSize.sm
        })
      ).pipe(
        catchError((err) => of(false)),
        switchMap((value) => of(false))
      );
    }

    return this.requestPermission(hids);
  }

  private requestPermission(hids: string[]): Observable<any> {
    return this.tfaService.handleTFA(this.auth.requestPermissions({ hids })).pipe(
      catchError((err) => {
        return of(false);
      })
    );
  }

  // @TODO Get rid of this dirty hack after 2FA won't use computer's hid
  private getAnyComputerFromAvailableComputerListStream(): Observable<string> {
    return this.computersFacade.getComputers({ appIds: [AgentType.RMM], limit: 1 }).pipe(
      map((response) => {
        if (response?.data?.length) {
          this.hidForTFA$.next(response.data[0].hid);
          return response.data[0].hid;
        }

        return null;
      })
    );
  }
}
